import { CompanyWrite } from "src/api/client-api/company/useCreateOrganization";
import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";

import { z } from "zod";

export class CompanySettingsForm {
    static getSupplierToCompanyGridForm = (existingSupplier: Company | undefined): CompanyGridForm => {
        if (!existingSupplier)
            return {
                name: "",
                isProvider: false,
                organizationType: "1",
                email: "",
                registrationNumber: "",
                externalId: "",
            };

        return {
            id: existingSupplier.id,
            name: existingSupplier.name,
            isProvider: existingSupplier.isProvider || false,
            organizationType: existingSupplier.organizationType?.id?.toString() || "1",
            address1: existingSupplier.address1,
            address2: existingSupplier.address2,
            zipcode: existingSupplier.zipcode,
            city: existingSupplier.city,
            state: existingSupplier.state,
            country: existingSupplier.country,
            phone: existingSupplier.phone,
            email: existingSupplier.email ?? "",
            clientId: existingSupplier.clientId,
            registrationNumber: existingSupplier.registrationNumber ?? "",
            externalId: existingSupplier.externalId ?? "",
            organizationSector: existingSupplier.organizationSector?.["@id"],
            parents: Array.isArray(existingSupplier.parents) ? existingSupplier.parents.map((p) => p["@id"]) : [],
            tags: existingSupplier.tags && !Array.isArray(existingSupplier.tags) ? existingSupplier.tags : {},
            jsonAttributes: existingSupplier.jsonAttributes || {},
        };
    };

    static getCompanyGridFormToCompanyWrite = (form: CompanyGridForm): CompanyWrite => {
        // Filter out empty string values from jsonAttributes to avoid validation errors
        const filteredJsonAttributes =
            form.jsonAttributes ?
                Object.fromEntries(
                    Object.entries(form.jsonAttributes).filter(
                        ([_, value]) => value !== "" && value !== null && value !== undefined,
                    ),
                )
            :   {};

        const companyWrite: CompanyWrite = {
            name: form.name,
            isProvider: form.isProvider,
            organizationType: form.organizationType ? `/api/organization_types/${form.organizationType}` : null,
            address1: form.address1,
            address2: form.address2,
            zipcode: form.zipcode,
            city: form.city,
            state: form.state,
            country: form.country,
            phone: form.phone,
            email: form.email,
            clientId: form.clientId,
            registrationNumber: form.registrationNumber,
            externalId: form.externalId,
            organizationSector: form.organizationSector || null,
            parents: form.parents || [],
            tags: form.tags || {},
            jsonAttributes: filteredJsonAttributes,
        };

        // Only include id if it exists (for updates)
        if (form.id !== undefined) {
            companyWrite.id = form.id;
        }

        return companyWrite;
    };
}

export const CompanyGridFormZodSchema = z.object({
    id: z.number().optional(),
    name: z.string().min(1),
    clientId: z.string().optional(),
    registrationNumber: z.string().optional(),
    externalId: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    zipcode: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    organizationSector: z.string().optional(),
    isProvider: z.boolean(),
    organizationType: z.string(),
    parents: z.array(z.string()).optional(),
    tags: z.record(z.string(), z.array(z.string())).optional(),
    jsonAttributes: z.record(z.string(), z.any()).optional(),
});

export type CompanyGridForm = z.infer<typeof CompanyGridFormZodSchema>;
